/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  EXECUTE: 'execute',
  SUCCESS: 'success',
  FAILED: 'failed',
  ERROR: 'error'
};

const API_TYPE = {
  EXECUTE: 'execute',
  MONITOR: 'monitor',
  TERMINATE: 'terminate',
  DISPOSE: 'dispose'
};

export default {
  name: 'requirement178',
  data() {
    return {
      phase: PHASE.START,
      isDispose: true,
      isLoading: false,
      systemId: '',
      modal_requirement_178: false,
      exitPromise: null,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダーカラーをフェーズで切り替える
     * @returns {string} ヘッダーカラー
     */
    headerColor() {
      const colorMap = {
        [PHASE.START]: 'secondary',
        [PHASE.EXECUTE]: 'secondary',
        [PHASE.SUCCESS]: 'success',
        [PHASE.FAILED]: 'danger',
        [PHASE.ERROR]: 'danger'
      };
      return colorMap[this.phase];
    },

    /**
     * ダイアログのヘッダータイトルをフェーズで切り替える
     * @returns {string} ヘッダータイトル
     */
    headerTitle() {
      const titleMap = {
        [PHASE.START]: 'message.common_title_confirmation',
        [PHASE.EXECUTE]: 'individual.common_title_execution',
        [PHASE.SUCCESS]: 'message.common_title_success',
        [PHASE.FAILED]: 'message.common_title_error',
        [PHASE.ERROR]: 'message.common_title_error'
      };
      return this.$t(titleMap[this.phase]);
    },

    /**
     * フェーズに応じてダイアログに表示する文言を切り替える
     * @returns {string} ダイアログに表示する文言
     */
    workSupportMessage() {
      const messageMap = {
        [PHASE.START]: `${this.i18nWsKey}.REQ_WS_178_TXT_1`,
        [PHASE.EXECUTE]: `${this.i18nWsKey}.REQ_WS_178_TXT_2_0001`,
        [PHASE.SUCCESS]: `${this.i18nWsKey}.REQ_WS_178_TXT_3_0002`,
        [PHASE.FAILED]: `${this.i18nWsKey}.REQ_WS_178_TXT_4_0010`,
        [PHASE.ERROR]: 'individual.error_communication_error'
      };
      return this.$t(messageMap[this.phase]);
    },

    /**
     * キャンセルボタンの表示・非表示を制御する
     * @returns {boolean} キャンセルボタンを表示するかどうか
     */
    showButtonCancel() {
      return this.phase === PHASE.START;
    },

    /**
     * OKボタンの表示・非表示を制御する
     * @returns {boolean} OKボタンを表示するかどうか
     */
    showButtonOk() {
      return this.phase !== PHASE.EXECUTE;
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /**
     * 初期化処理
     */
    init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.phase = PHASE.START;
      this.isDispose = true;
      this.modal_requirement_178 = true;
    },

    /**
     * Wizardを閉じる
     */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_178 = false;
    },

    /**
     * 共通エラー画面を表示する
     */
    showUnexpectedError() {
      this.phase = PHASE.ERROR;
    },

    /**
     * REST応答からcleanup分岐に使用するis_disposeを保存する。
     * @param {object} response APIのレスポンス
     * @returns {boolean} is_disposeを保存できたかどうか
     */
    storeIsDispose(response) {
      if (typeof response?.data?.user_data?.is_dispose !== 'boolean') {
        return false;
      }

      this.isDispose = response.data.user_data.is_dispose;
      return true;
    },

    /**
     * isDisposeに応じてAPIを実行後に、指定された画面へ遷移する。
     * @param {string} phase 遷移先の画面フェーズ
     */
    async cleanup(phase) {
      this.exitPromise = this.isDispose ? this.dispose() : this.terminate();

      try {
        await this.exitPromise;
      } finally {
        this.phase = phase;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {function} onUnexpectedError 予期せぬエラー発生時のコールバック
     */
    validateErrorResult(result, onUnexpectedError = () => { }) {
      apiErrorDefaultAction(
        this,
        result,
        () => { this.logout(this); },
        () => { this.resetSession(this); },
        () => { onUnexpectedError(); },
      );
    },

    /**
     * 指定されたシーケンスのAPIをポーリングする。
     * @param {string} type execute, monitor, terminate or dispose
     * @param {function} onUnexpectedError 予期せぬエラー発生時のコールバック
     * @returns {Promise<object>} APIのレスポンス
     */
    async polling(type, onUnexpectedError = () => { }) {
      const token = session.getToken();

      return await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        { id_token: token.id_token, type },
        onUnexpectedError,
        result => this.validateErrorResult(result, onUnexpectedError),
        result => this.validateErrorResult(result, onUnexpectedError),
      );
    },

    /**
     * キャンセルボタン押下時処理
     */
    onCancelClicked() {
      this.closeWizard();
    },

    /**
     * OKボタン押下時処理
     */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.START:
          await this.execute();
          break;
        case PHASE.SUCCESS:
        case PHASE.FAILED:
        case PHASE.ERROR:
          this.closeWizard();
          break;
        default:
          break;
      }
    },

    /**
     * ルーチン実行
     */
    async execute() {
      try {
        this.isLoading = true;

        const response = await this.polling(
          API_TYPE.EXECUTE,
          () => this.showUnexpectedError(),
        );

        if (!response) {
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        if (!this.storeIsDispose(response)) {
          // FE/BE間の契約が守られていれば発生しない。
          await this.showUnexpectedError();
          return;
        }

        if (response.data?.status === REQUEST_STATUS_FAILED) {
          await this.cleanup(PHASE.ERROR);
          return;
        }

        // この時点でルーチン実行まで正常終了しているので、実行監視に移る。
        await this.monitor();
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * ルーチン結果確認
     */
    async monitor() {
      try {
        this.isLoading = true;
        this.phase = PHASE.EXECUTE;

        const response = await this.polling(
          API_TYPE.MONITOR,
          () => {
            this.exitPromise = this.cleanup(PHASE.ERROR);
          },
        );

        if (!response) {
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        switch (response.data?.status) {
          case REQUEST_STATUS_COMPLETED:
            await this.cleanup(PHASE.SUCCESS);
            break;
          case REQUEST_STATUS_FAILED:
            if (response.data?.async_code === error.WSP_178_FUNCTIONAL_ERROR) {
              await this.cleanup(PHASE.FAILED);
            } else {
              await this.cleanup(PHASE.ERROR);
            }
            break;
          default:
            await this.cleanup(PHASE.ERROR);
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 終了処理
     */
    async terminate() {
      try {
        this.isLoading = true;

        await this.polling(API_TYPE.TERMINATE);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * dispose
     */
    async dispose() {
      try {
        this.isLoading = true;

        await this.polling(API_TYPE.DISPOSE);
      } finally {
        this.isLoading = false;
      }
    }
  },

  /**
   * コンポーネントがマウントされた際に呼び出されるライフサイクルフック
   */
  mounted() {
    this.init();
  }
};
